import { Injectable, signal, computed, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { LoginService } from './login.service';
import { TranslateService } from '@ngx-translate/core';
import { aiUnavailableMessageKey } from './ai-availability.service';

export type ChatMode = 'query' | 'help' | 'protocol-help' | 'plan' | 'template';

/** A qué está anclada una sesión. El `kind` deja sitio a otros anclajes sin rehacer el tipo. */
export interface ChatSessionContext {
  kind: 'mailing-template';
  mailingId: number;
  beeTemplateId: string;
}

export interface ChatPluginCapability {
  name: string;
  functionCount: number;
}

export interface PendingActionParam {
  name: string;
  label: string;
  value: any;
}

export interface PendingAction {
  actionId: string;
  actionType: string;
  description: string;
  displayName: string;
  parameters: PendingActionParam[];
  status: 'pending' | 'approved' | 'denied' | 'executed' | 'error';
  resultMessage?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  mode?: ChatMode;
  feedback?: 'like' | 'dislike';
  pendingAction?: PendingAction;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  autoApprovedActions: string[];
  disabledPlugins: string[];
  context?: ChatSessionContext;
}

interface ChatRequestMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatRequest {
  chatSessionId: string;
  messages: ChatRequestMessage[];
  mode: ChatMode;
  autoApprovedActions?: string[];
  disabledPlugins?: string[];
  context?: ChatSessionContext;
}

interface ChatFeedbackRequest {
  chatSessionId: string;
  messageId: string;
  vote: 'like' | 'dislike';
  mode: ChatMode;
  question: string;
  answer: string;
  comment: string;
}

interface ChatCapabilitiesResponse {
  enabled: boolean;
  query: boolean;
  help: boolean;
  protocolHelp: boolean;
  plan: boolean;
  /**
   * Solo `true` si la empresa tiene clave CSAPI **y** el usuario tiene acceso al plugin
   * `PlantillasBee` (permiso `MAILINGS_WRITE`); sin cualquiera de las dos, el backend rechaza el
   * modo con 503 antes de gastar un turno de LLM.
   */
  template?: boolean;
  plugins?: ChatPluginCapability[];
}

export interface FeedbackPeriodMetrics {
  period: string;
  totalVotes: number;
  likes: number;
  dislikes: number;
  approvalRate: number;
}

export interface DislikedIntentMetrics {
  question: string;
  count: number;
}

export interface FeedbackMetricsSnapshot {
  daily: FeedbackPeriodMetrics;
  weekly: FeedbackPeriodMetrics;
  monthly: FeedbackPeriodMetrics;
  topDislikedIntents: DislikedIntentMetrics[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly loginService = inject(LoginService);
  private readonly translate = inject(TranslateService);

  visible = signal<boolean>(false);
  sessions = signal<ChatSession[]>([]);
  activeSessionId = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  capabilities = signal<ChatCapabilitiesResponse>({
    enabled: true,
    query: true,
    help: true,
    protocolHelp: true,
    plan: true,
    template: false,
    plugins: []
  });
  availablePlugins = computed<ChatPluginCapability[]>(() =>
    [...(this.capabilities().plugins ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  );

  activeSession = computed(() =>
    this.sessions().find(s => s.id === this.activeSessionId()) || null
  );

  availableModes = computed<{ label: string; value: ChatMode }[]>(() => {
    const capabilities = this.capabilities();
    const modes: { label: string; value: ChatMode }[] = [];
    if (capabilities.query) {
      modes.push({ label: 'Query', value: 'query' });
    }
    if (capabilities.help) {
      modes.push({ label: 'Help', value: 'help' });
    }
    if (capabilities.protocolHelp) {
      modes.push({ label: 'Protocol Help', value: 'protocol-help' });
    }
    if (capabilities.plan) {
      modes.push({ label: 'Plan', value: 'plan' });
    }
    // El modo Diseño solo se ofrece con capacidades Y sesión anclada: sin anclaje el backend
    // rechaza la petición antes de gastar un turno, así que ofrecerlo sería una trampa. Al
    // soltarse el anclaje (salir del editor, caducar la sesión) el modo desaparece solo y el
    // sidebar vuelve a `query`.
    if (capabilities.template && this.activeSession()?.context) {
      modes.push({ label: 'Diseño', value: 'template' });
    }
    return modes;
  });

  messages = computed(() => this.activeSession()?.messages || []);

  constructor() {
    if (this.sessions().length === 0) {
      this.startNewSession();
    }

    void this.loadCapabilities();
  }

  toggle() {
    this.visible.update(v => !v);
  }

  startNewSession() {
    const newSession: ChatSession = {
      id: crypto.randomUUID(),
      title: 'Nuevo Chat',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      autoApprovedActions: [],
      disabledPlugins: []
    };

    this.sessions.update(current => [newSession, ...current]);
    this.activeSessionId.set(newSession.id);
  }

  selectSession(sessionId: string) {
    this.activeSessionId.set(sessionId);
  }

  /** Ata una sesión a lo que se está editando. El agente no puede trabajar sin esto. */
  anchorSession(sessionId: string, context: ChatSessionContext) {
    this.sessions.update(sessions =>
      sessions.map(session => session.id === sessionId ? { ...session, context } : session));
  }

  /**
   * Suelta el anclaje cuando la sesión del editor caduca. La conversación se conserva: al
   * siguiente mensaje, quien monta el editor pedirá una sesión nueva y volverá a anclar.
   */
  releaseSessionAnchor(sessionId: string) {
    this.sessions.update(sessions =>
      sessions.map(session => {
        if (session.id !== sessionId) return session;
        const { context, ...rest } = session;
        return rest;
      }));
  }

  deleteSession(sessionId: string) {
    this.sessions.update(current => current.filter(s => s.id !== sessionId));
    if (this.activeSessionId() === sessionId) {
      const remaining = this.sessions();
      if (remaining.length > 0) {
        this.activeSessionId.set(remaining[0].id);
      } else {
        this.startNewSession();
      }
    }
  }

  async sendMessage(content: string, mode: ChatMode = 'query'): Promise<void> {
    const currentSessionId = this.activeSessionId();
    if (!currentSessionId || !content.trim() || this.isLoading()) {
      return;
    }

    const allowedModes = new Set(this.availableModes().map(m => m.value));
    if (!allowedModes.has(mode)) {
      throw new Error('El modo seleccionado no esta disponible actualmente.');
    }

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
      mode
    };

    this.updateSessionMessages(currentSessionId, userMessage);

    const requestMessages = this.buildRequestMessages(currentSessionId);
    const session = this.sessions().find(s => s.id === currentSessionId);

    const assistantMessageId = crypto.randomUUID();
    const assistantPlaceholder: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      mode
    };

    this.updateSessionMessages(currentSessionId, assistantPlaceholder);
    this.isLoading.set(true);

    try {
      await this.ensureSessionFresh('/api/chat');
      await this.streamChatResponse(
        {
          chatSessionId: currentSessionId,
          messages: requestMessages,
          mode,
          autoApprovedActions: session?.autoApprovedActions,
          disabledPlugins: session?.disabledPlugins,
          context: session?.context
        },
        chunk => {
          this.appendToMessage(currentSessionId, assistantMessageId, chunk);
        },
        currentSessionId,
        assistantMessageId
      );

      const finalMessage = this.getMessageById(currentSessionId, assistantMessageId);
      if (!finalMessage?.content.trim()) {
        this.replaceMessageContent(
          currentSessionId,
          assistantMessageId,
          'No he recibido contenido del servidor. Intentalo de nuevo.'
        );
      } else {
        const enriched = this.enrichWithLinks(finalMessage.content);
        if (enriched !== finalMessage.content) {
          this.replaceMessageContent(currentSessionId, assistantMessageId, enriched);
        }
      }
    } catch (error: any) {
      const fallback = this.normalizeError(error);
      this.replaceMessageContent(currentSessionId, assistantMessageId, fallback);
    } finally {
      this.isLoading.set(false);
    }
  }

  async confirmAction(sessionId: string, messageId: string, actionId: string): Promise<void> {
    this.updatePendingActionStatus(sessionId, messageId, 'approved');

    try {
      await this.ensureSessionFresh('/api/chat/confirm-action');

      const language = this.loginService.get('language') || 'ES';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'language-code': language
      };

      const response = await fetch(`${environment.config.api_url}chat/confirm-action`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ actionId })
      });

      if (!response.ok) {
        const errorMsg = await this.readErrorResponse(response);
        this.updatePendingAction(sessionId, messageId, { status: 'error', resultMessage: errorMsg });
        return;
      }

      const result = await response.json() as { success: boolean; message: string };
      this.updatePendingAction(sessionId, messageId, {
        status: result.success ? 'executed' : 'error',
        resultMessage: result.message
      });
    } catch (error: any) {
      const errorMsg = this.normalizeError(error);
      this.updatePendingAction(sessionId, messageId, { status: 'error', resultMessage: errorMsg });
    }
  }

  denyAction(sessionId: string, messageId: string): void {
    this.updatePendingActionStatus(sessionId, messageId, 'denied');
  }

  toggleAutoApprove(sessionId: string, actionType: string, enabled: boolean): void {
    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        const actions = new Set(session.autoApprovedActions);
        if (enabled) {
          actions.add(actionType);
        } else {
          actions.delete(actionType);
        }

        return { ...session, autoApprovedActions: [...actions] };
      })
    );
  }

  togglePlugin(sessionId: string, pluginName: string, enabled: boolean): void {
    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        const disabled = new Set(session.disabledPlugins);
        if (enabled) {
          disabled.delete(pluginName);
        } else {
          disabled.add(pluginName);
        }

        return { ...session, disabledPlugins: [...disabled] };
      })
    );
  }

  isPluginEnabled(sessionId: string, pluginName: string): boolean {
    const session = this.sessions().find(s => s.id === sessionId);
    if (!session) {
      return true;
    }

    return !session.disabledPlugins.includes(pluginName);
  }

  private async ensureSessionFresh(url: string): Promise<void> {
    if (this.loginService.shouldRefreshToken(url)) {
      await this.loginService.refresh();
    }
  }

  async loadCapabilities(): Promise<void> {
    try {
      await this.ensureSessionFresh('/api/chat/capabilities');
      const language = this.loginService.get('language') || 'ES';
      const headers: Record<string, string> = { 'language-code': language };

      const response = await fetch(`${environment.config.api_url}chat/capabilities`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        return;
      }

      const capabilities = await response.json() as ChatCapabilitiesResponse;
      this.capabilities.set(capabilities);
    } catch {
      // Keep defaults on connectivity errors.
    }
  }

  private async streamChatResponse(
    request: ChatRequest,
    onChunk: (chunk: string) => void,
    sessionId: string,
    assistantMessageId: string
  ): Promise<void> {
    const language = this.loginService.get('language') || 'ES';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'language-code': language
    };

    const response = await fetch(`${environment.config.api_url}chat`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(await this.readErrorResponse(response));
    }

    if (!response.body) {
      const bodyText = await response.text();
      this.processSseText(bodyText, onChunk, sessionId, assistantMessageId);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = this.consumeSseEvents(buffer, onChunk, sessionId, assistantMessageId);
    }

    const tail = buffer.trim();
    if (tail.length > 0) {
      this.processSseText(tail, onChunk, sessionId, assistantMessageId);
    }
  }

  private consumeSseEvents(
    buffer: string,
    onChunk: (chunk: string) => void,
    sessionId: string,
    assistantMessageId: string
  ): string {
    let current = buffer;
    while (true) {
      const separatorIndex = current.indexOf('\n\n');
      if (separatorIndex === -1) {
        return current;
      }

      const eventBlock = current.slice(0, separatorIndex);
      current = current.slice(separatorIndex + 2);

      this.processSseText(eventBlock, onChunk, sessionId, assistantMessageId);
    }
  }

  private processSseText(
    eventBlock: string,
    onChunk: (chunk: string) => void,
    sessionId: string,
    assistantMessageId: string
  ): void {
    const lines = eventBlock.split('\n');
    let eventType: string | null = null;

    for (const rawLine of lines) {
      // Sin trim: el modelo trocea el texto por cualquier sitio y un trozo puede acabar o empezar en espacio
      // («o » + «ver»). Solo se quita el \r de un fin de línea CRLF; tras «data:» se descarta un único espacio.
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (!line.trim()) {
        continue;
      }

      // Detect SSE named events
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
        continue;
      }

      if (!line.startsWith('data:')) {
        continue;
      }

      const payload = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
      if (!payload) {
        continue;
      }

      // Handle named events (action_proposal, action_executed)
      if (eventType === 'action_proposal' || eventType === 'action_executed') {
        try {
          const actionData = JSON.parse(payload);
          const pendingAction: PendingAction = {
            actionId: actionData.actionId,
            actionType: actionData.actionType,
            displayName: actionData.displayName,
            description: actionData.description,
            parameters: actionData.parameters || [],
            status: eventType === 'action_executed'
              ? (actionData.status === 'executed' ? 'executed' : 'error')
              : 'pending',
            resultMessage: actionData.resultMessage
          };
          this.setMessagePendingAction(sessionId, assistantMessageId, pendingAction);
        } catch {
          // Ignore malformed action events
        }
        eventType = null;
        continue;
      }

      // Reset event type for regular data lines
      eventType = null;

      if (payload.startsWith('{')) {
        try {
          const parsed = JSON.parse(payload);
          if (parsed?.error) {
            throw new Error(String(parsed.error));
          }
          if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
            throw new Error(parsed.errors.join(' | '));
          }
        } catch (err: any) {
          if (err instanceof Error) {
            throw err;
          }
        }
      }

      onChunk(payload.replace(/\\n/g, '\n'));
    }
  }

  private async readErrorResponse(response: Response): Promise<string> {
    try {
      const json = await response.json();
      const aiKey = aiUnavailableMessageKey(json);
      if (aiKey) {
        return this.translate.instant(aiKey);
      }
      if (json?.error) {
        return String(json.error);
      }
      if (Array.isArray(json?.errors) && json.errors.length > 0) {
        return json.errors.join(' | ');
      }
      if (json?.message) {
        return String(json.message);
      }
    } catch {
      // ignore non-json
    }

    return `Error al contactar con el asistente (HTTP ${response.status}).`;
  }

  private normalizeError(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return 'No se pudo completar la conversacion con el asistente.';
  }

  private buildRequestMessages(sessionId: string): ChatRequestMessage[] {
    const session = this.sessions().find(s => s.id === sessionId);
    if (!session) {
      return [];
    }

    return session.messages
      .filter(m => m.role === 'user' || (m.role === 'assistant' && m.content.trim().length > 0))
      .map(m => ({ role: m.role, content: m.content }));
  }

  private updateSessionMessages(sessionId: string, newMessage: ChatMessage) {
    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        const updatedMessages = [...session.messages, newMessage];
        let title = session.title;

        if (session.messages.length === 0 && newMessage.role === 'user') {
          title = newMessage.content.slice(0, 30) + (newMessage.content.length > 30 ? '...' : '');
        }

        return {
          ...session,
          messages: updatedMessages,
          title,
          updatedAt: new Date()
        };
      })
    );
  }

  private appendToMessage(sessionId: string, messageId: string, chunk: string) {
    if (!chunk) {
      return;
    }

    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        return {
          ...session,
          messages: session.messages.map(msg =>
            msg.id === messageId
              ? { ...msg, content: `${msg.content}${chunk}`, timestamp: new Date() }
              : msg
          ),
          updatedAt: new Date()
        };
      })
    );
  }

  private replaceMessageContent(sessionId: string, messageId: string, content: string) {
    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        return {
          ...session,
          messages: session.messages.map(msg =>
            msg.id === messageId
              ? { ...msg, content, timestamp: new Date() }
              : msg
          ),
          updatedAt: new Date()
        };
      })
    );
  }

  private setMessagePendingAction(sessionId: string, messageId: string, pendingAction: PendingAction) {
    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        return {
          ...session,
          messages: session.messages.map(msg =>
            msg.id === messageId
              ? { ...msg, pendingAction }
              : msg
          ),
          updatedAt: new Date()
        };
      })
    );
  }

  private updatePendingActionStatus(
    sessionId: string,
    messageId: string,
    status: PendingAction['status']
  ) {
    this.updatePendingAction(sessionId, messageId, { status });
  }

  private updatePendingAction(
    sessionId: string,
    messageId: string,
    updates: Partial<PendingAction>
  ) {
    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }

        return {
          ...session,
          messages: session.messages.map(msg => {
            if (msg.id !== messageId || !msg.pendingAction) {
              return msg;
            }
            return {
              ...msg,
              pendingAction: { ...msg.pendingAction, ...updates }
            };
          }),
          updatedAt: new Date()
        };
      })
    );
  }

  private getMessageById(sessionId: string, messageId: string): ChatMessage | null {
    const session = this.sessions().find(s => s.id === sessionId);
    if (!session) {
      return null;
    }

    return session.messages.find(m => m.id === messageId) || null;
  }

  private enrichWithLinks(content: string): string {
    return content
      .replace(/Evento\s+#(\d+)/g, '[Evento #$1](/events/$1/edit)')
      .replace(/Persona\s+#(\d+)/g, '[Persona #$1](/people/$1/edit)')
      .replace(/Tarea\s+#(\d+)/g, '[Tarea #$1](/tasks/$1/edit)')
      .replace(/Grupo\s+#(\d+)/g, '[Grupo #$1](/groups/$1/edit)')
      .replace(/Inscripcion\s+#(\d+)/g, '[Inscripcion #$1](/inscriptions/$1)')
      .replace(/Correspondencia\s+#(\d+)/g, '[Correspondencia #$1](/correspondence/$1/edit)');
  }

  setFeedback(sessionId: string, messageId: string, feedback: 'like' | 'dislike') {
    let appliedFeedback: 'like' | 'dislike' | undefined;
    let payload: ChatFeedbackRequest | null = null;

    this.sessions.update(current =>
      current.map(session => {
        if (session.id !== sessionId) {
          return session;
        }
        const message = session.messages.find(msg => msg.id === messageId);
        const toggledFeedback = message?.feedback === feedback ? undefined : feedback;
        appliedFeedback = toggledFeedback;

        if (message && toggledFeedback) {
          payload = {
            chatSessionId: session.id,
            messageId: message.id,
            vote: toggledFeedback,
            mode: message.mode ?? 'query',
            question: this.findQuestionForAnswer(session.messages, message.id),
            answer: message.content,
            comment: ''
          };
        }

        return {
          ...session,
          messages: session.messages.map(msg => {
            if (msg.id === messageId) {
              return {
                ...msg,
                feedback: toggledFeedback
              };
            }
            return msg;
          })
        };
      })
    );

    if (!appliedFeedback || !payload) {
      return;
    }

    void this.sendFeedback(payload);
  }

  private findQuestionForAnswer(messages: ChatMessage[], assistantMessageId: string): string {
    const answerIndex = messages.findIndex(m => m.id === assistantMessageId);
    if (answerIndex <= 0) {
      return '';
    }

    for (let i = answerIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return messages[i].content;
      }
    }

    return '';
  }

  private async sendFeedback(request: ChatFeedbackRequest): Promise<void> {
    await this.ensureSessionFresh('/api/chat/feedback');

    const language = this.loginService.get('language') || 'ES';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'language-code': language
    };

    const response = await fetch(`${environment.config.api_url}chat/feedback`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      // Keep UX optimistic: feedback is optional and must not block chat interactions.
      console.warn('No se pudo registrar feedback de chat', response.status);
    }
  }

  async getFeedbackMetrics(
    mode?: 'query' | 'help' | 'protocol-help' | 'plan',
    mineOnly: boolean = false
  ): Promise<FeedbackMetricsSnapshot> {
    await this.ensureSessionFresh('/api/chat/feedback/metrics');

    const language = this.loginService.get('language') || 'ES';
    const headers: Record<string, string> = {
      'language-code': language
    };

    const url = new URL(`${environment.config.api_url}chat/feedback/metrics`);
    if (mode) {
      url.searchParams.set('mode', mode);
    }
    url.searchParams.set('mineOnly', String(mineOnly));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(await this.readErrorResponse(response));
    }

    return await response.json() as FeedbackMetricsSnapshot;
  }
}
